import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';

const Footer = ({ onOpenContactForm }) => {
  const year = new Date().getFullYear();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer data-testid="main-footer" className="bg-[#1E1B4B] text-white relative overflow-hidden">
      {/* Top gradient line */}
      <div className="h-1 bg-gradient-to-r from-[#6366F1] via-[#A855F7] to-[#6366F1]" />

      {/* CTA Banner */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
        <div className="bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-2xl p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display',serif" }}>
              Ready to Protect Your Brand?
            </h2>
            <p className="text-sm text-white/70 font-normal">Get started today — free consultation, no commitment required.</p>
          </div>
          <Button data-testid="footer-cta-button" onClick={onOpenContactForm} className="bg-white text-[#6366F1] hover:bg-white/90 font-semibold px-8 py-5 text-sm rounded-full shrink-0 shadow-lg group">
            Get Started <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        <div className="border-t border-white/[0.08] pt-12 pb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="sm:col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src="/logo-white.png" alt="Trademark Symbol Font" className="h-10 w-auto" />
              </div>
              <p className="text-sm text-white/50 leading-relaxed font-normal">Expert federal trademark registration with precision and transparency.</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/30 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {[{ label: 'How It Works', id: 'process' }, { label: 'Pricing', id: 'pricing' }, { label: 'Insights', id: 'blog' }, { label: 'Reviews', id: 'testimonials' }, { label: 'FAQ', id: 'faq' }].map(link => (
                  <li key={link.id}>
                    <button data-testid={`footer-link-${link.id}`} onClick={() => scrollTo(link.id)} className="text-sm text-white/60 hover:text-[#A78BFA] transition-colors flex items-center gap-1 group">
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/30 mb-4">Contact</h4>
              <ul className="space-y-3">
                <li><a href="tel:+18001234567" className="text-sm text-white/60 hover:text-[#A78BFA] transition-colors flex items-center gap-2.5"><Phone className="w-4 h-4 text-[#A78BFA] shrink-0" strokeWidth={1.5} />+1 (800) 123-4567</a></li>
                <li><a href="mailto:info@trademarksymbolfont.com" className="text-sm text-white/60 hover:text-[#A78BFA] transition-colors flex items-start gap-2.5 break-all"><Mail className="w-4 h-4 text-[#A78BFA] shrink-0 mt-0.5" strokeWidth={1.5} />info@trademarksymbolfont.com</a></li>
              </ul>
            </div>

            {/* Address */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/30 mb-4">Office</h4>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#A78BFA] shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-sm text-white/60 font-normal leading-relaxed">3031 Tisch Way 110 PW,<br />San Jose, CA 95128</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/[0.06] pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/30">&copy; {year} Trademark Symbol Font. All rights reserved.</p>
          <div className="flex gap-5 text-xs">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-white/30 hover:text-[#A78BFA] transition-colors">Privacy Policy</Link>
            <Link to="/terms-conditions" data-testid="footer-terms-link" className="text-white/30 hover:text-[#A78BFA] transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
